import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import authProfileApi from '../api/auth-profile-api';
import { Permission } from '@/shared/types/permissions.types';
import {
  syncPermissionsRequest,
  syncPermissionsSuccess,
  syncPermissionsFailure,
  updatePermissions,
  updateUserRole,
  clearPermissions,
} from '../store/slices/permissions-slice';
import { loginSuccess, logoutSuccess } from '@/features/security/sagas/auth-slice';
import { RootState } from '../store/store';
import { UserRole } from '../types/permissions.types';

/**
 * Saga para sincronizar permisos del usuario
 */
function* syncPermissionsSaga() {
  try {
    
    // 1. Obtener perfil completo del usuario
    const profile = yield call(authProfileApi.getProfile);
    
    // 2. Actualizar estado de permisos
    yield put(syncPermissionsSuccess({
      permissions: profile.permissions,
      userRole: profile.userRole,
      timestamp: profile.lastSync,
    }));
    
    
  } catch (error) {
    yield put(syncPermissionsFailure(error.message || 'Error sincronizando permisos'));
  }
}

/**
 * Saga para manejar login exitoso - sincronizar permisos automáticamente
 */
function* handleLoginSuccessSaga(action: PayloadAction<{
  user: any;
  token: string | null;
  refreshToken: string | null;
}>) {
  try {
    
    // Esperar un momento para que se complete el login
    yield call(() => new Promise(resolve => setTimeout(resolve, 100)));
    
    // Sincronizar permisos
    yield call(syncPermissionsSaga);
    
  } catch (error) {
    console.warn("Sincronización de permisos post-login no completada:", error);
  }
}

/**
 * Saga para manejar logout - limpiar permisos
 */
function* handleLogoutSuccessSaga() {
  try {
    
    // Limpiar permisos del estado
    yield put(clearPermissions());
    
    
  } catch (error) {
    console.warn("Limpieza de permisos post-logout no completada:", error);
  }
}

/**
 * Saga para verificar permisos en tiempo real
 */
function* checkPermissionsSaga() {
  try {
    // Obtener estado actual
    const state: RootState = yield select();
    const { lastSync } = state.permissions;
    
    // Si no hay sincronización reciente (más de 5 minutos), resincronizar
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    
    if (!lastSync || lastSync < fiveMinutesAgo) {
      yield put(syncPermissionsRequest());
    }
    
  } catch (error) {
    console.warn("Chequeo periódico de vigencia de permisos no completado:", error);
  }
}

/**
 * Saga para manejar actualizaciones de permisos en tiempo real
 */
function* handlePermissionsUpdateSaga(action: PayloadAction<{
  permissions: string[];
  userRole?: UserRole;
}>) {
  try {
    
    // Actualizar permisos
    yield put(updatePermissions(action.payload.permissions as Permission[]));
    
    // Si cambió el rol, actualizarlo también
    if (action.payload.userRole) {
      yield put(updateUserRole(action.payload.userRole));
    }
    
    
  } catch (error) {
    // Si el `put` de una actualizacion en tiempo real falla, la app se queda
    // con los permisos/rol ANTERIORES sin que nadie se entere. Se despacha el
    // mismo failure que syncPermissionsSaga para que quede en el estado
    // (state.permissions.error) en vez de perderse en consola.
    yield put(syncPermissionsFailure(
      error instanceof Error ? error.message : 'Error aplicando actualizacion de permisos en tiempo real'
    ));
  }
}

/**
 * Watcher sagas
 */
export function* permissionsWatcherSaga() {
  // Sincronización manual de permisos
  yield takeLatest(syncPermissionsRequest.type, syncPermissionsSaga);
  
  // Sincronización automática después del login
  yield takeLatest(loginSuccess.type, handleLoginSuccessSaga);
  
  // Limpiar permisos después del logout
  yield takeLatest(logoutSuccess.type, handleLogoutSuccessSaga);
  
  // Verificar permisos periódicamente
  yield takeEvery('PERMISSIONS_CHECK_REQUEST', checkPermissionsSaga);
  
  // Actualizaciones en tiempo real
  yield takeEvery('PERMISSIONS_UPDATE_REAL_TIME', handlePermissionsUpdateSaga);
}

export default permissionsWatcherSaga;
